import { Anchor, Box, Button, Grid, Group, Text, Title } from '@mantine/core';
import { IconBulb, IconChevronRight } from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import useFetch from '../../helpers/useFetch';
import ErrorMessage from '../ErrorMessage';
import Spinner from '../Spinner';
import ActivateDomainSubscriptionCard from '../Subscriptions/ActivateDomainSubscriptionCard';
import SubscriptionSelectorForm from '../Subscriptions/SubscriptionSelectorForm';

const ActivateDomainSubscriptionTab = ({ domain, handleChangeTab }) => {
  // What the domain is on, or is in the middle of taking on. 'inactive' rows are
  // cancelled plans kept as history (a Shopify uninstall parks the domain's
  // subscriptions there), and counting them here would tell a returning merchant
  // they had already chosen: the flagship recommendation below only appears for a
  // domain with no subscriptions at all, and the plan list is filtered down to
  // "what you already have" for accounts that cannot manage their own plans.
  //
  // Memoised so the identity is stable: it feeds a useCallback that a fetching
  // effect depends on, and a fresh array every render would refetch the plan
  // list in a loop.
  const currentSubscriptions = useMemo(
    () => domain.domainSubscriptions.filter((s) => s.status !== 'inactive'),
    [domain.domainSubscriptions]
  );

  const [subscriptions, setSubscriptions] = useState();
  const [selectedSubscriptions, setSelectedSubscriptions] = useState(currentSubscriptions.map((s) => s.appliedValues));
  const [loading, setLoading] = useState();
  const [error, setError] = useState();
  const [explorePlans, setExplorePlans] = useState();

  const fetch = useFetch();
  const isSelected = useCallback(
    (subscription) => {
      return currentSubscriptions.some((ds) => ds.appliedValues.id === subscription.id);
    },
    [currentSubscriptions]
  );

  useEffect(() => {
    fetch(`/api/admin/subscriptions`)
      .then(({ subscriptions }) => {
        setSubscriptions(subscriptions.filter((s) => domain.account.canManageSubscriptions || isSelected(s)));
      })
      .catch((error) => {
        setError(error);
      });
  }, [domain.account.canManageSubscriptions, fetch, isSelected]);

  useEffect(() => {
    const initialFlagshipSubscription = subscriptions?.find((s) => s.type === 'flagship');
    if (initialFlagshipSubscription && currentSubscriptions.length === 0) {
      setSelectedSubscriptions([initialFlagshipSubscription]);
    }
  }, [currentSubscriptions.length, subscriptions]);

  if (!subscriptions) {
    return <Spinner />;
  }

  const topSubscription = subscriptions.find((s) => s.type === 'flagship');

  const selectSubscription = () => {
    setLoading(true);
    fetch(`/api/admin/accounts/${domain.accountId}/domains/${domain.id}/subscriptions/confirmations`, {
      method: 'POST',
      body: JSON.stringify({ selectedSubscriptions }),
    })
      .then(({ confirmationUrl }) => {
        if (confirmationUrl) {
          window.location.href = confirmationUrl;
        } else {
          handleChangeTab({ name: 'subscription', status: 'completed' });
        }
      })
      .catch(setError)
      .finally(() => {
        setLoading(false);
      });
  };

  if (!topSubscription) {
    return (
      <Title order={3}>
        No subscriptions are available for your shop. Please contact{' '}
        <Anchor href="mailto:support@releva.ai">support@releva.ai</Anchor> to proceed.
      </Title>
    );
  }

  return (
    <>
      <ErrorMessage message={error?.message} />

      {explorePlans ? (
        <SubscriptionSelectorForm
          subscriptions={subscriptions}
          setSelectedSubscriptions={setSelectedSubscriptions}
          selectedSubscriptions={selectedSubscriptions}
        />
      ) : (
        <>
          <Text component="h5" c="dimmed" ta="center">
            Based on the information you have provided, we have picked the best plan for you:
          </Text>

          <Grid>
            <Grid.Col span={{ base: 12, sm: 3 }}></Grid.Col>
            <ActivateDomainSubscriptionCard
              subscription={topSubscription}
              isSelected={true}
              toggleSelected={() => {}}
            />
          </Grid>

          <Box ta="center">
            <Text component="h5" c="dimmed">...or</Text>
            <Button
              variant="default"
              disabled={selectedSubscriptions.length === 0 || loading}
              onClick={() => setExplorePlans(true)}
            >
              <IconBulb size={14} /> Explore Other Options
            </Button>
          </Box>
        </>
      )}

      <Group justify="flex-end" mt="md">
        <Button
          disabled={selectedSubscriptions.length === 0 || loading}
          onClick={() => selectSubscription()}
          rightSection={<IconChevronRight size={14} />}
        >
          Next
        </Button>
      </Group>
    </>
  );
};

export default ActivateDomainSubscriptionTab;
